// The label dashboard's release cards (ART-335) — one release, two sizes:
//
//   • `ReleaseMiniCards` — All roster: a card per artist (their soonest upcoming release, else their most recent).
//     The whole card opens that artist's dashboard.
//   • `ArtistReleaseCard` — the artist page: the same release as the full card, its tiles opening the release's tabs
//     and its timeline under them.
//
// Built from the shared release pieces so a card reads exactly like the release it opens: `CoverBleed` (the record's
// own artwork as the ground, as on the releases hub and builder), `DarkHero`, `DarkStatTile`, `ReleaseArtwork`, and
// the releases page's own formatters (`countdownBlock`, `releaseTypeLabel`, `formatMonthDay`, `formatDollars`).
// Everything on it is the server's (`/roster/release-cards`, `/bands/{id}/release-card`); nothing is re-derived here
// but the words.
import clsx from "clsx";
import { ArrowRight } from "lucide-react";
import { Button, Card, CoverBleed, DarkHero, DarkStatTile, Icon, coverBleedHost } from "@cntrl/ui";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import type { ReleaseCardStep, RosterLastRelease, RosterReleaseCard } from "../../app/hooks/useReleaseCards.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import {
  daysToRelease,
  formatDollars,
  formatMonthDay,
  formatNumericDate,
  releaseGradient,
  releaseTypeLabel,
} from "../releases/releaseFormat.js";
import type { ReleaseSectionKey } from "../releases/sections.js";
import styles from "./ReleaseCards.module.scss";

/** The card's cover, resolved from the ARTIST's band (All roster mounts no band) — upload first, then the hotlink. */
function useCoverUrl(card: RosterReleaseCard): string | null {
  const cover = useMediaObjectUrl(
    card.coverMediaId ?? null,
    card.coverMediaId ? null : (card.coverImageUrl ?? null),
    { variant: "sm", bandId: card.bandId },
  );
  return cover.error ? null : (cover.url ?? null);
}

/** The next timeline step, as the mini card's footer says it. */
function nextStepLine(card: RosterReleaseCard): string {
  const next = (card.timeline ?? []).find((s) => s.state === "next");
  if (next) return `Next: ${next.name} · ${formatMonthDay(next.date)}`;
  return card.upcoming ? "On track" : "In cycle";
}

/** The days count and what it counts — "Days out" / "Days in" on the small card, "Days to release" / "Days since
 *  release" on the large one. Release day itself has no number: "Out today". */
function countdown(card: RosterReleaseCard, size: "mini" | "full"): { value: number | null; label: string } {
  const n = daysToRelease(card.releaseDate);
  if (n === null) return { value: null, label: "Date TBD" };
  if (n === 0) return { value: null, label: "Out today" };
  if (n > 0) return { value: n, label: size === "mini" ? "Days out" : "Days to release" };
  return { value: -n, label: size === "mini" ? "Days in" : "Days in cycle" };
}

function todoValue(card: RosterReleaseCard): string {
  return card.openTodos > 0 ? `${card.openTodos} open` : "Done";
}

// ---- All roster ---------------------------------------------------------------------------------------------

export function ReleaseMiniCards({
  cards,
  onOpenArtist,
}: {
  cards: readonly RosterReleaseCard[];
  onOpenArtist: (bandId: string) => void;
}) {
  if (cards.length === 0) return null;
  return (
    <section className={styles.miniGrid} aria-label="Releases">
      {cards.map((card) => (
        <ReleaseMiniCard key={card.releaseId} card={card} onOpen={() => onOpenArtist(card.bandId)} />
      ))}
    </section>
  );
}

function ReleaseMiniCard({ card, onOpen }: { card: RosterReleaseCard; onOpen: () => void }) {
  const coverUrl = useCoverUrl(card);
  const count = countdown(card, "mini");
  const assetsPct = card.assetsTotal > 0 ? Math.round((card.assetsAdded / card.assetsTotal) * 100) : 0;
  const tiles: { label: string; value: string }[] = [
    { label: "Assets", value: card.assetsTotal > 0 ? `${card.assetsAdded}/${card.assetsTotal}` : "—" },
    { label: "To-do", value: todoValue(card) },
    { label: "Budget", value: card.budget ? formatDollars(card.budget.spentCents) : "—" },
  ];
  return (
    <button
      type="button"
      className={clsx(styles.mini, coverBleedHost)}
      // No artwork ⇒ the record's own born-blank gradient, the one `ReleaseArtwork` paints for it.
      style={coverUrl ? undefined : { background: releaseGradient(card.releaseId) }}
      onClick={onOpen}
      aria-label={`${card.artistName}: ${card.title}. Open ${card.artistName}`}
    >
      <CoverBleed url={coverUrl} />
      <span className={styles.miniHead}>
        <ReleaseArtwork
          seed={card.releaseId}
          mediaId={card.coverMediaId}
          imageUrl={card.coverImageUrl}
          bandId={card.bandId}
          size="tile"
          className={styles.miniArt}
          lazy={false}
        />
        <span className={styles.miniText}>
          <span className={styles.miniArtist}>{card.artistName}</span>
          <span className={styles.miniTitle}>{card.title}</span>
          <span className={styles.miniMeta}>
            {releaseTypeLabel(card.releaseType)} · {formatNumericDate(card.releaseDate)}
          </span>
        </span>
        <span className={styles.miniCount}>
          {count.value !== null && <span className={styles.miniCountNum}>{count.value}</span>}
          <span className={styles.miniCountLabel}>{count.label}</span>
        </span>
      </span>
      <span className={styles.miniTiles}>
        {tiles.map((t) => (
          <span key={t.label} className={styles.miniTile}>
            <span className={styles.miniTileLabel}>{t.label}</span>
            <span className={styles.miniTileValue}>{t.value}</span>
          </span>
        ))}
      </span>
      <span className={styles.bar} aria-hidden="true">
        <span className={styles.barFill} style={{ inlineSize: `${assetsPct}%` }} />
      </span>
      <span className={styles.miniFoot}>
        <span className={styles.miniNext}>{nextStepLine(card)}</span>
        <span className={styles.miniOpen}>Open →</span>
      </span>
    </button>
  );
}

// ---- the artist page ----------------------------------------------------------------------------------------

export function ArtistReleaseCard({
  card,
  onOpenRelease,
}: {
  card: RosterReleaseCard;
  onOpenRelease: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  const coverUrl = useCoverUrl(card);
  const count = countdown(card, "full");
  const open = (section?: ReleaseSectionKey) => () => onOpenRelease(card.releaseId, section);
  const budget = card.budget;
  const timeline = card.timeline ?? [];
  return (
    <DarkHero
      className={styles.hero}
      photoUrl={coverUrl}
      photoTreatment="bleed"
      mediaLayout="head"
      titleSize="lg"
      subtitle={<span className={styles.heroMeta}>{formatNumericDate(card.releaseDate)}</span>}
      media={
        <ReleaseArtwork
          seed={card.releaseId}
          mediaId={card.coverMediaId}
          imageUrl={card.coverImageUrl}
          size="tile"
          lazy={false}
        />
      }
      eyebrow={card.upcoming ? "Up next" : "In cycle"}
      title={card.title}
      titleId="artist-release-card-title"
      action={
        <div className={styles.heroCount}>
          {count.value !== null && <span className={styles.heroCountNum}>{count.value}</span>}
          <span className={styles.heroCountLabel}>{count.label}</span>
          <Button variant="ghost" size="sm" className={styles.heroOpen} onClick={open()}>
            Open release <Icon icon={ArrowRight} size={16} />
          </Button>
        </div>
      }
    >
      {/* Pre-release work only (product owner): the smart link is a post-release number, so it is not here. The
          next to-do leads, twice the width of the two figures beside it. */}
      {card.upcoming ? (
        // Up next (pre-release): the next to-do leads, twice the width of the two figures beside it. The smart
        // link is a post-release number, so it is not here (product owner, ART-335).
        <div className={styles.heroTiles}>
          <NextTodoTile card={card} onOpen={open("todo")} />
          <DarkStatTile
            label="Assets in"
            value={card.assetsTotal > 0 ? card.assetsAdded : null}
            suffix={card.assetsTotal > 0 ? ` / ${card.assetsTotal}` : undefined}
            exact
            sub={assetsSub(card)}
            emptySub="No assets listed"
            onClick={open("assets")}
          />
          {budget && (
            <DarkStatTile
              label="Budget"
              value={Math.round((budget.spentCents ?? 0) / 100)}
              prefix="$"
              exact
              sub={budget.totalCents != null
                ? `${formatDollars(budget.totalCents - (budget.spentCents ?? 0))} left of ${formatDollars(budget.totalCents)}`
                : "spent"}
              onClick={open("budget")}
            />
          )}
        </div>
      ) : (
        // In cycle (out): the smart link and ad spend are what a release is doing now (ART-332). Ad spend is
        // marketing money, so its tile is there only with the budget block.
        <div className={styles.heroTilesEven}>
          <DarkStatTile
            label="Assets in"
            value={card.assetsTotal > 0 ? card.assetsAdded : null}
            suffix={card.assetsTotal > 0 ? ` / ${card.assetsTotal}` : undefined}
            exact
            sub={assetsSub(card)}
            emptySub="No assets listed"
            onClick={open("assets")}
          />
          <DarkStatTile
            label="To-do"
            value={card.openTodos > 0 ? card.openTodos : null}
            suffix={card.openTodos > 0 ? " open" : undefined}
            exact
            sub={card.overdueTodos > 0 ? `${card.overdueTodos} overdue` : null}
            emptySub="All done"
            onClick={open("todo")}
          />
          <DarkStatTile
            label="Smart link · 30d"
            value={card.smartLinkVisits30d > 0 ? card.smartLinkVisits30d : null}
            sub="visits"
            emptySub="no visits yet"
            onClick={open("smart-link")}
          />
          {budget && (
            <DarkStatTile
              label="Ad spend"
              value={(budget.adSpendCents ?? 0) > 0 ? Math.round((budget.adSpendCents ?? 0) / 100) : null}
              prefix="$"
              exact
              sub={`${budget.adCampaigns ?? 0} ${(budget.adCampaigns ?? 0) === 1 ? "campaign" : "campaigns"}`}
              emptySub={`${budget.adCampaigns ?? 0} ${(budget.adCampaigns ?? 0) === 1 ? "campaign" : "campaigns"}`}
              onClick={open("ads")}
            />
          )}
        </div>
      )}
      {timeline.length > 0 && <ReleaseTimeline steps={timeline} />}
    </DarkHero>
  );
}

/** "2 to go · 1 in progress" — what is still missing, and how much of it is under way. */
function assetsSub(card: RosterReleaseCard): string | null {
  const toGo = card.assetsTotal - card.assetsAdded;
  if (toGo <= 0) return "All in";
  return [`${toGo} to go`, card.assetsInProgress > 0 ? `${card.assetsInProgress} in progress` : null]
    .filter(Boolean).join(" · ");
}

/** The release's next open to-do: what it is, who it is with, and when it is due (amber; red once overdue). */
function NextTodoTile({ card, onOpen }: { card: RosterReleaseCard; onOpen: () => void }) {
  const next = card.nextTodo;
  const overdue = next?.dueDate != null && (daysToRelease(next.dueDate) ?? 0) < 0;
  return (
    <button type="button" className={styles.nextTodo} onClick={onOpen}>
      <span className={styles.nextTodoLabel}>Next to-do</span>
      {next ? (
        <>
          <span className={styles.nextTodoRow}>
            <span className={styles.nextTodoTitle}>{next.title}</span>
            {next.dueDate && (
              <span className={styles.nextTodoDue} data-overdue={overdue || undefined}>
                {formatMonthDay(next.dueDate)}
              </span>
            )}
          </span>
          <span className={styles.nextTodoSub}>
            {next.assigneeName ? `Assigned to ${next.assigneeName}` : "Unassigned"}
            {card.openTodos > 1 ? ` · ${card.openTodos - 1} more open` : ""}
          </span>
        </>
      ) : (
        <span className={styles.nextTodoSub}>Nothing open</span>
      )}
    </button>
  );
}

/** The release's milestones as dots on a line: done solid, next ringed, upcoming an outline. */
function ReleaseTimeline({ steps }: { steps: readonly ReleaseCardStep[] }) {
  return (
    <ol className={styles.timeline} aria-label="Timeline">
      {steps.map((s, i) => (
        <li key={`${s.date}-${i}`} className={styles.step} data-state={s.state}>
          <span className={styles.stepTrack} aria-hidden="true">
            <span className={styles.stepDot} />
            {i < steps.length - 1 && <span className={styles.stepLine} />}
          </span>
          <span className={styles.stepDate}>{formatMonthDay(s.date)}</span>
          <span className={styles.stepName}>{s.name}</span>
        </li>
      ))}
    </ol>
  );
}

// ---- the artist page: the last release --------------------------------------------------------------------------

/** The artist's most recent release already out, with its smart-link numbers — the Releases page's own figures. */
export function LastReleaseStrip({ release, onOpen }: { release: RosterLastRelease; onOpen: (releaseId: string) => void }) {
  const stats: { label: string; value: string }[] = [
    { label: "Visits · 30d", value: (release.visits30d ?? 0).toLocaleString() },
    { label: "DSP clicks", value: (release.dspClicks30d ?? 0).toLocaleString() },
    { label: "Visit → click", value: release.visitToClickPct == null ? "—" : `${release.visitToClickPct.toFixed(1)}%` },
  ];
  return (
    <Card>
      <section className={styles.last} aria-label="Last release">
        <ReleaseArtwork
          seed={release.releaseId}
          mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl}
          size="tile"
          className={styles.lastArt}
        />
        <span className={styles.lastText}>
          <span className={styles.lastEyebrow}>Last release</span>
          <span className={styles.lastTitle}>{release.title}</span>
          <span className={styles.lastMeta}>
            {releaseTypeLabel(release.releaseType)} · out {formatMonthDay(release.releaseDate)} · day {release.daysSince}
          </span>
        </span>
        <span className={styles.lastStats}>
          {stats.map((st) => (
            <span key={st.label} className={styles.lastStat}>
              <span className={styles.lastStatLabel}>{st.label}</span>
              <span className={styles.lastStatValue}>{st.value}</span>
            </span>
          ))}
        </span>
        <Button variant="ghost" size="sm" onClick={() => release.releaseId && onOpen(release.releaseId)}>
          Open
        </Button>
      </section>
    </Card>
  );
}
